// src/components/Navbar.js
import React, { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiGlobe } from 'react-icons/fi';
import { FaSearch } from 'react-icons/fa';
import '../styles/Navbar.css';
import { T, useGT, useLocale } from '../lib/gt-shim';
import { API_ENDPOINTS, apiRequest } from '../config/api';

// Normalize anything like "en-US", "en_us", "ENG" → "en".
const baseLocale = (l) => (l || 'en').toLowerCase().split(/[-_]/)[0];

const Navbar = ({ isLoggedIn, onOpenLanguagePicker, showLanguageTrigger = true }) => {
  const t = useGT();
  const locale = useLocale();
  const localeBadge = baseLocale(locale).toUpperCase();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = React.useRef(null);
  
  // Check if we're on the search page to hide the search bar
  const isSearchPage = location.pathname === '/search';
  // Check if we're on home page (hero already provides its own search field)
  const isHomePage = location.pathname === '/';
  // The in-nav search FORM is shown everywhere except home (the hero owns the
  // search) and the search page (its own filters own it). On those two routes
  // the search SLOT is still rendered — just empty — so the surrounding nav
  // elements keep their exact positions and the nav doesn't jump on
  // navigation. See `.navbar-search--empty` in Navbar.css.
  const showNavSearch = !isSearchPage && !isHomePage;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm('');
    }
  };

  const handleLogout = async () => {
    try {
      await apiRequest(API_ENDPOINTS.AUTH.LOGOUT, { method: 'POST', credentials: 'include' });
      window.location.href = '/';
    } catch (err) {
      alert('Logout failed: ' + err.message);
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMenu}>
          <img src="/logo.svg" alt="" className="navbar-logo-img" />
          <span className="navbar-wordmark">518 Cares</span>
        </Link>

        {/* In-nav search SLOT. Always rendered so the surrounding nav elements
            never reflow between routes; on home + search the slot is present
            but empty (and aria-hidden) so it only reserves space. The actual
            search form renders on every other route. */}
        <div
          className={showNavSearch ? 'navbar-search' : 'navbar-search navbar-search--empty'}
          aria-hidden={showNavSearch ? undefined : true}
        >
          {showNavSearch && (
            <form onSubmit={handleSearchSubmit}>
              <div className="search-box">
                <div className="search-icon">
                  <FaSearch size={16} />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="search-input"
                  placeholder={t('Search resources')}
                  aria-label={t('Search resources')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="clear-search"
                    onClick={() => setSearchTerm('')}
                    aria-label={t('Clear search')}
                  >
                    <FiX size={16} />
                  </button>
                )}
              </div>
              <button type="submit" className="search-submit" style={{display: 'none'}}>
                <T>Search</T>
              </button>
            </form>
          )}
        </div>

        <div className="menu-icon" onClick={toggleMenu}>
          {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </div>

        <ul className={isMenuOpen ? 'nav-menu active' : 'nav-menu'}>
          <li className="nav-item">
            <NavLink 
              to="/search" 
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              onClick={closeMenu}
            >
              <T>Search</T>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink 
              to="/eligibility" 
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              onClick={closeMenu}
            >
              <T>Eligibility</T>
            </NavLink>
          </li>
          <li className="nav-item">
            <NavLink 
              to="/about" 
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              onClick={closeMenu}
            >
              <T>About</T>
            </NavLink>
          </li>
        </ul>

        <div className="navbar-actions">
          {/* Pass-7: single language affordance. The globe trigger opens the
              first-visit LanguagePicker modal (App.js renders the modal).
              Replaces the EN|ES inline toggle from pass-5 and the bottom
              <LocaleSelector /> — one clear way to choose a language,
              presented up front, persisted to localStorage. */}
          {onOpenLanguagePicker && showLanguageTrigger && (
            <button
              type="button"
              className="language-trigger"
              onClick={onOpenLanguagePicker}
              aria-label={t('Language')}
              title={t('Language')}>
              <span className="language-trigger__icon" aria-hidden="true">
                <FiGlobe />
              </span>
              <span className="language-trigger__label">{localeBadge}</span>
            </button>
          )}
          {isLoggedIn && (
            <button className="logout-btn" onClick={handleLogout} style={{ marginLeft: 16 }}>
              <T>Logout</T>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;